import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../feature/user.Slice";
import { useNavigate } from "react-router-dom";
import HidePasswordIcon from "../components/HidePasswordIcon";
import {
  executeRecaptcha,
  hasRecaptchaEnterprise,
  loadRecaptchaEnterpriseScript,
} from "../utils/recaptchaEnterprise";

/**
 * Signin est une page qui permet à l'utilisateur de se connecter à son compte.
 * Elle contient un formulaire où l'utilisateur peut entrer son nom d'utilisateur et son mot de passe.
 * Lorsque le formulaire est soumis, une requête POST est envoyée à l'API pour authentifier l'utilisateur.
 * Si l'authentification réussit, l'utilisateur est redirigé vers la page d'accueil.
 * Si l'authentification échoue, un message d'erreur est affiché.
 *
 */
const apiPrefix = process.env.REACT_APP_API_PREFIX;

const regexEmail = /^[^@\s]{2,30}@[^\s@]{2,30}\.[^@\s]{2,}$/;
const regexPassword =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*+.~/-]).{8,}$/;

const Signin = () => {
  const Numberapp = useSelector((state) => state.app.appNumber);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    return loadRecaptchaEnterpriseScript();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Suppression des espaces avant et après les entrées utilisateur
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();

    if (!regexEmail.test(trimmedUsername)) {
      setError("Le nom d'utilisateur doit être une adresse e-mail valide.");
      return;
    }
    if (!regexPassword.test(trimmedPassword)) {
      setError(
        "Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial."
      );
      return;
    }

    if (!hasRecaptchaEnterprise(window.grecaptcha)) {
      setError("reCAPTCHA non disponible. Veuillez recharger la page.");
      return;
    }

    const formData = {
      username: trimmedUsername,
      password: trimmedPassword,
    };

    try {
      const captchaToken = await executeRecaptcha();
      const response = await fetch(apiPrefix + "login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          token: captchaToken,
          appNumber: Numberapp,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        if (data.user.active) {
          dispatch(setUser({ user: data.user, token: data.token }));
          navigate("/");
        } else {
          setError("Veuillez contacter notre support à l'adresse suivante : .");
        }
      } else {
        let data = {};
        try {
          data = await response.json();
        } catch (parseError) {
          data = {};
        }
        if (data.error && /recaptcha/i.test(data.error)) {
          setError(data.error);
        } else {
          setError("Nom d'utilisateur ou mot de passe incorrect");
        }
      }
    } catch (error) {
      console.error("Erreur:", error);
      if (/recaptcha/i.test(error?.message || "")) {
        setError(
          "Erreur avec reCAPTCHA. Veuillez recharger la page et réessayer."
        );
      }
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 md:py-12 px-4 sm:px-6 lg:px-8 theme-background">
      <div className="max-w-md w-full space-y-6 md:space-y-8 mx-auto">
        <div className="card-theme transform transition-all duration-200 hover:scale-[1.01]">
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-theme-primary mb-3 md:mb-4 tracking-tight">
              Connexion
            </h2>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 mt-4 text-sm md:text-base">
                <i className="fas fa-exclamation-circle mr-2"></i>
                {error}
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="w-full space-y-5 md:space-y-6"
          >
            <div>
              <label
                htmlFor="username"
                className="block text-sm md:text-base font-semibold text-gray-800 mb-2 md:mb-3"
              >
                <i className="fas fa-envelope mr-2 text-theme-accent"></i>
                Mail d'utilisateur :
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 md:py-3.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-base input-theme"
                placeholder="votre@email.com"
                required
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm md:text-base font-semibold text-gray-800 mb-2 md:mb-3"
              >
                <i className="fas fa-key mr-2 text-theme-accent"></i>
                Mot de passe :
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 md:py-3.5 pr-12 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-theme-primary transition-all duration-200 text-base input-theme"
                  placeholder="Votre mot de passe"
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                  <HidePasswordIcon
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 md:space-y-4 pt-2">
              <button
                type="submit"
                className="w-full btn-theme py-3 md:py-3.5 text-base font-semibold"
              >
                <i className="fas fa-sign-in-alt mr-2"></i>
                Se connecter
              </button>

              <button
                type="button"
                className={`w-full transition-colors duration-200 text-sm md:text-base font-medium text-center py-2 ${
                  Numberapp === 6
                    ? "bg-theme-primary text-white hover:bg-theme-primary-active"
                    : "text-theme-primary hover:text-white"
                }`}
                onClick={handleForgotPassword}
              >
                Mot de passe oublié ?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signin;
